import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  LoadingState,
  SectionHeader,
  SelectField,
} from '@pixlide/ui';
import {
  useActivateVideoOverlayTemplate,
  useCreateVideoOverlayTemplate,
  useDeactivateVideoOverlayTemplate,
  useVideoOverlayTemplates,
} from '@/api/queries/video-overlay-templates';
import type { components } from '@/api/generated/schema';

type VideoOverlayTemplate =
  components['schemas']['PixlideApplicationFeaturesContentVideoOverlayTemplatesVideoOverlayTemplateAdminResponse'];

const positionLabels: Record<number, string> = {
  0: 'Haut gauche',
  1: 'Haut droite',
  2: 'Bas gauche',
  3: 'Bas droite',
};

const createTemplateSchema = z.object({
  name: z.string().min(1, 'Le nom est obligatoire.'),
  logoPosition: z.coerce.number().int().min(0).max(3),
  bannerText: z.string().optional(),
  bannerColor: z.string().optional(),
});

/**
 * Templates d'habillage vidéo (logo + bandeau) proposés aux clients lors de la création
 * d'une vidéo. Même structure que `PlansPage` : liste complète (actifs et inactifs),
 * activation/désactivation en un clic, formulaire de création avec dépôt direct du logo.
 */
export function VideoOverlayTemplatesPage() {
  const templates = useVideoOverlayTemplates();
  const createTemplate = useCreateVideoOverlayTemplate();
  const activateTemplate = useActivateVideoOverlayTemplate();
  const deactivateTemplate = useDeactivateVideoOverlayTemplate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | undefined>(undefined);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: { logoPosition: 3 },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!logo) {
      setLogoError('Le logo est obligatoire.');
      return;
    }

    setLogoError(undefined);

    await createTemplate.mutateAsync({
      name: values.name,
      logoPosition: values.logoPosition as components['schemas']['PixlideDomainContentEnumsVideoOverlayLogoPosition'],
      bannerText: values.bannerText || null,
      bannerColor: values.bannerColor || null,
      logo,
    });

    reset();
    setLogo(null);
    setShowCreateForm(false);
  });

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <SectionHeader
        title="Templates d'habillage"
        description="Logo et bandeau incrustés dans une vidéo, proposés aux clients lors de sa création."
        action={
          <Button
            variant={showCreateForm ? 'ghost' : 'primary'}
            size="sm"
            onClick={() => setShowCreateForm((current) => !current)}
          >
            {showCreateForm ? (
              <>
                <X aria-hidden className="size-4" />
                Annuler
              </>
            ) : (
              <>
                <Plus aria-hidden className="size-4" />
                Nouveau template
              </>
            )}
          </Button>
        }
      />

      {showCreateForm ? (
        <Card className="p-6">
          <form onSubmit={onSubmit} noValidate className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom" error={errors.name?.message} {...register('name')} />

            <SelectField label="Position du logo" {...register('logoPosition')}>
              {Object.entries(positionLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>

            <Field
              label="Texte du bandeau (optionnel)"
              error={errors.bannerText?.message}
              {...register('bannerText')}
            />

            <Field
              label="Couleur du bandeau (optionnel)"
              placeholder="#000000"
              error={errors.bannerColor?.message}
              {...register('bannerColor')}
            />

            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="text-[11px] leading-4 font-medium text-ink-300">Logo</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/avif"
                onChange={(event) => setLogo(event.target.files?.[0] ?? null)}
                className="text-sm text-ink-500"
              />
              {logoError ? (
                <p role="alert" className="text-xs font-medium text-danger-500">
                  {logoError}
                </p>
              ) : null}
            </div>

            <Button
              type="submit"
              isLoading={isSubmitting || createTemplate.isPending}
              className="sm:col-span-2"
            >
              Créer le template
            </Button>
          </form>
        </Card>
      ) : null}

      {templates.isPending ? <LoadingState label="Chargement des templates…" /> : null}

      {templates.isError ? (
        <ErrorState message="Impossible de charger les templates." onRetry={() => void templates.refetch()} />
      ) : null}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {(templates.data ?? []).map((template) => (
          <VideoOverlayTemplateCard
            key={template.id}
            template={template}
            onToggleActive={() => {
              if (template.isActive) {
                deactivateTemplate.mutate(template.id);
              } else {
                activateTemplate.mutate(template.id);
              }
            }}
            isToggling={activateTemplate.isPending || deactivateTemplate.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function VideoOverlayTemplateCard({
  template,
  onToggleActive,
  isToggling,
}: {
  template: VideoOverlayTemplate;
  onToggleActive: () => void;
  isToggling: boolean;
}) {
  return (
    <Card className="flex flex-col gap-3 p-6">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-lg font-semibold">{template.name}</h3>
        <Badge tone={template.isActive ? 'success' : 'neutral'}>
          {template.isActive ? 'Actif' : 'Inactif'}
        </Badge>
      </div>

      <img
        src={template.logoDownloadUrl}
        alt={template.name}
        className="h-20 w-full rounded-lg bg-ink-50 object-contain"
      />

      <p className="text-sm text-ink-500">Logo : {positionLabels[template.logoPosition]}</p>

      {template.bannerText ? (
        <p className="truncate text-xs text-ink-300">Bandeau : {template.bannerText}</p>
      ) : null}

      {template.logoUploaded ? null : (
        <p className="text-xs text-warning-500">Logo non confirmé</p>
      )}

      <Button
        variant={template.isActive ? 'secondary' : 'primary'}
        size="sm"
        isLoading={isToggling}
        disabled={!template.logoUploaded}
        onClick={onToggleActive}
        className="mt-auto"
      >
        {template.isActive ? 'Désactiver' : 'Activer'}
      </Button>
    </Card>
  );
}
